'use client';
import React, { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

import LinkButton from '@/components/atoms/linkButton';
import { findElClosestTheme } from '@/components/themeProviderUtils';

import IconTrustpilotBadge from '~/svg/IconTrustpilotBadge.svg';

declare const window: Window &
  typeof globalThis & {
    Trustpilot?: {
      loadFromElement: (el: HTMLElement, state: boolean) => void;
    };
  };

interface TrustpilotBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  classNames?: {
    root: string;
  };
}

export const TrustpilotBadge = ({ classNames }: TrustpilotBadgeProps) => {
  const truspilotWrapperElRef = useRef<HTMLDivElement>(null);
  const [theme, setTheme] = React.useState('light');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const script = document.createElement('script');
      script.src =
        'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
      script.async = true;
      script.onload = () => {
        if (truspilotWrapperElRef.current) {
          window.Trustpilot?.loadFromElement(
            truspilotWrapperElRef.current,
            true
          );
        }
      };
      document.body.appendChild(script);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const foundTheme = findElClosestTheme(
      truspilotWrapperElRef?.current as HTMLElement
    );
    if (foundTheme) {
      setTheme(foundTheme);
    }
  }, []);

  return (
    <div
      className={cn('trustpilot-widget', classNames?.root)}
      data-locale='en-GB'
      data-template-id='53aa8807dec7e10d38f59f32'
      data-businessunit-id='67f91a41d68b154e79a253a4'
      data-style-height='36px'
      data-style-width='90px'
      data-theme={theme === 'dark' ? 'dark' : 'light'}
      data-tracker-component='trustpilot-badge'
      ref={truspilotWrapperElRef}
    >
      <LinkButton
        href='https://uk.trustpilot.com/review/smartproxy.com'
        rel='noopener noreferrer nofollow'
        target='_blank'
        slim
        variant='text'
      >
        <IconTrustpilotBadge className='text-neutral-N99' />
      </LinkButton>
    </div>
  );
};
